import { onMounted, onUnmounted } from 'vue'
import { useAuthStore } from '@/stores/auth'
import { supabase } from '@/lib/supabase'

/**
 * Composable for monitoring session validity
 * Automatically checks session validity and handles expiration
 */
export function useSessionMonitor() {
  const authStore = useAuthStore()
  let sessionCheckInterval: NodeJS.Timeout | null = null

  const startSessionMonitoring = () => {
    // Check session more frequently on localhost (every 2 minutes) vs production (every 5 minutes)
    const isLocalhost = window.location.hostname === 'localhost'
    const checkInterval = isLocalhost ? 2 * 60 * 1000 : 5 * 60 * 1000 // 2 minutes for localhost, 5 minutes for production
    
    console.log(`🔍 Starting session monitoring (${isLocalhost ? 'localhost' : 'production'} mode, checking every ${isLocalhost ? '2' : '5'} minutes)`)
    
    // Immediate check on start
    setTimeout(async () => {
      if (authStore.session && !authStore.isAuthenticated) {
        console.log('🔍 Immediate session restoration check...')
        await authStore.restoreUserSession()
      } else if (authStore.user && !authStore.session) {
        console.log('🔍 Immediate session recovery check...')
        try {
          const { data: { session: currentSession } } = await supabase.auth.getSession()
          if (currentSession) {
            console.log('✅ Found current session, updating...')
            authStore.session = currentSession
          }
        } catch (error) {
          console.error('Failed to get current session:', error)
        }
      }
    }, 1000)
    
    sessionCheckInterval = setInterval(async () => {
      if (authStore.session) {
        console.log('🔍 Periodic session check...')
        const isValid = await authStore.checkSessionValidity()
        if (!isValid) {
          console.log('🔒 Session expired during periodic check, stopping monitoring')
          stopSessionMonitoring()
        }
      }
    }, checkInterval)
  }

  const stopSessionMonitoring = () => {
    if (sessionCheckInterval) {
      clearInterval(sessionCheckInterval)
      sessionCheckInterval = null
    }
  }

  onMounted(() => {
    startSessionMonitoring()
  })

  onUnmounted(() => {
    stopSessionMonitoring()
  })

  return {
    startSessionMonitoring,
    stopSessionMonitoring
  }
}
